import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { Timer, PlayCircle, PauseCircle, StopCircle, ChevronLeft, ChevronRight, Flag, PlusCircle, MinusCircle, Maximize, Minimize, CheckSquare } from 'lucide-react';
import { playSound } from '../utils/sound';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';

const MatchPlayPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    matches, updateMatch, currentMatchId, setCurrentMatchId, tournament, soundEnabled, teams
  } = useAppContext();

  const [remainingTime, setRemainingTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showHomeScorers, setShowHomeScorers] = useState(false);
  const [showAwayScorers, setShowAwayScorers] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const timerRef = useRef(null);
  const match = matches.find(m => m.id === id) || null;

  // Effect to handle fullscreen changes (e.g., ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    // Cleanup listener on unmount
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Redirect if no match or tournament
  useEffect(() => {
    if (!match || !tournament) {
      navigate('/schedule');
    }
  }, [match, tournament, navigate]);

  // Initialize timer - only run when match ID or duration changes
  useEffect(() => {
    if (match) {
      // Store match ID and duration to track actual changes
      const matchId = match.id;
      const matchDuration = match.duration;

      // Only reset the timer if we don't already have a time set
      // or if the match ID or duration changed
      if (remainingTime === 0 || !timerRef.current) {
        setRemainingTime(matchDuration * 60); // Convert minutes to seconds
      }
    }
  }, [match?.id, match?.duration]);

  // Timer logic
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          // When 1 minute remaining
          if (prev === 61 && soundEnabled) {
            playSound('warning', soundEnabled);
          }

          // When time is up
          if (prev === 1) {
            clearInterval(timerRef.current);
            setIsRunning(false);
            if (soundEnabled) playSound('whistle', soundEnabled);
            return 0;
          }

          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isRunning, soundEnabled]);

  // Add one minute to timer
  const addOneMinute = () => {
    if (match.status === 'completed') return;
    setRemainingTime(prev => prev + 60);
  };

  // Remove one minute from timer
  const removeOneMinute = () => {
    if (match.status === 'completed') return;
    setRemainingTime(prev => Math.max(0, prev - 60)); // Ensure time doesn't go below 0
  };

  // Function to handle timer click - allows manual time adjustment
  const handleTimerClick = () => {
    if (match.status === 'completed') return;

    // Prompt for new time value in seconds
    const newTimeStr = prompt('Enter remaining time in seconds:', remainingTime.toString());
    if (newTimeStr === null) return; // User cancelled

    const newTime = parseInt(newTimeStr);
    if (!isNaN(newTime) && newTime >= 0) {
      setRemainingTime(newTime);
    }
  };

  if (!match) return null;

  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start match
  const startMatch = () => {
    if (match.status === 'scheduled') {
      updateMatch(match.id, { status: 'in-progress' });
    }
    setIsRunning(true);
  };

  // Pause match
  const pauseMatch = () => {
    setIsRunning(false);
  };

  // Reset timer
  const resetTimer = () => {
    setIsRunning(false);
    setRemainingTime(match.duration * 60);
  };

  // End match - Always return to schedule
  const endMatch = () => {
    if (!match) return; // Safety check

    // 1. Mark current match as completed
    updateMatch(match.id, { status: 'completed' });
    setIsRunning(false);
    setShowEndConfirm(false);

    // 2. Always navigate back to the schedule page
    console.log(`Ending match ${match.id}. Navigating back to schedule.`);
    // Optionally clear currentMatchId if desired, though schedule page might reset it
    // setCurrentMatchId(null); 
    navigate('/schedule');
  };

  // Show scorer selection popup for home team
  const handleHomeGoalClick = () => {
    console.log("Home goal button clicked");
    // Get the most up-to-date team data from the global state
    const currentHomeTeam = teams.find(t => t.id === match.homeTeam?.id);
    console.log("Checking Current Home Team Data:", JSON.stringify(currentHomeTeam, null, 2));

    // Check if there are any players on the CURRENT team data
    const homePlayers = currentHomeTeam?.players || [];
    console.log(`Found ${homePlayers.length} home players.`);

    if (!homePlayers || homePlayers.length === 0) {
      // No players to choose from, add an anonymous goal
      console.log('No players on home team, adding anonymous goal');
      addGoal('home', null, match.homeScore + 1);
      return;
    }
    
    // We have players, show the selection popup
    console.log(`Showing selection popup for ${homePlayers.length} home team players`);
    setShowHomeScorers(true);
    
    // Alert to ensure we know the popup should be showing
    console.log("HOME TEAM POPUP TRIGGERED - should be visible now");
  };

  // Show scorer selection popup for away team
  const handleAwayGoalClick = () => {
    console.log("Away goal button clicked");
    // Get the most up-to-date team data from the global state
    const currentAwayTeam = teams.find(t => t.id === match.awayTeam?.id);
    console.log("Checking Current Away Team Data:", JSON.stringify(currentAwayTeam, null, 2));

    // Check if there are any players on the CURRENT team data
    const awayPlayers = currentAwayTeam?.players || [];
    console.log(`Found ${awayPlayers.length} away players.`);

    if (!awayPlayers || awayPlayers.length === 0) {
      // No players to choose from, add an anonymous goal
      console.log('No players on away team, adding anonymous goal');
      addGoal('away', null, match.awayScore + 1);
      return;
    }
    
    // We have players, show the selection popup
    console.log(`Showing selection popup for ${awayPlayers.length} away team players`);
    setShowAwayScorers(true);
    
    // Alert to ensure we know the popup should be showing
    console.log("AWAY TEAM POPUP TRIGGERED - should be visible now");
  };

  // Generic function to add a goal for either team
  const addGoal = (team, playerId, updatedScore) => {
    console.log(`Adding ${team} goal for player ${playerId || 'anonymous'} with new score: ${updatedScore}`);
    
    // Create the goal event
    const goalEvent = {
      type: 'goal',
      team: team,
      time: Date.now(),
      playerId: playerId
    };
    
    // Determine which score to update
    const updates = {
      events: [...match.events, goalEvent]
    };
    
    if (team === 'home') {
      updates.homeScore = updatedScore || (match.homeScore + 1);
    } else {
      updates.awayScore = updatedScore || (match.awayScore + 1);
    }
    
    // Update the match with new score and event
    updateMatch(match.id, updates);
    
    // Close any open popups
    setShowHomeScorers(false);
    setShowAwayScorers(false);
    
    // Play sound effect if enabled
    if (soundEnabled) playSound('goal', soundEnabled);
    
    console.log(`Goal added successfully for ${team} team`);
  };

  // Add home goal with specific player ID
  const addHomeGoal = (playerId) => {
    addGoal('home', playerId, match.homeScore + 1);
  };

  // Add away goal with specific player ID
  const addAwayGoal = (playerId) => {
    addGoal('away', playerId, match.awayScore + 1);
  };

  // Remove goal for home team
  const removeHomeGoal = () => {
    if (match.homeScore <= 0) return;
    
    // Find the last home goal event
    const homeGoalEvents = match.events.filter(e => e.type === 'goal' && e.team === 'home');
    if (homeGoalEvents.length === 0) return;
    
    const lastHomeGoalEvent = homeGoalEvents[homeGoalEvents.length - 1];
    
    updateMatch(match.id, {
      homeScore: match.homeScore - 1,
      events: match.events.filter(e => e !== lastHomeGoalEvent)
    });
  };

  // Remove goal for away team
  const removeAwayGoal = () => {
    if (match.awayScore <= 0) return;
    
    // Find the last away goal event
    const awayGoalEvents = match.events.filter(e => e.type === 'goal' && e.team === 'away');
    if (awayGoalEvents.length === 0) return;
    
    const lastAwayGoalEvent = awayGoalEvents[awayGoalEvents.length - 1];
    
    updateMatch(match.id, {
      awayScore: match.awayScore - 1,
      events: match.events.filter(e => e !== lastAwayGoalEvent)
    });
  };

  // Navigate to previous match
  const goToPreviousMatch = () => {
    const currentIndex = matches.findIndex(m => m.id === match.id);
    if (currentIndex > 0) {
      const prevMatch = matches[currentIndex - 1];
      setCurrentMatchId(prevMatch.id);
      navigate(`/match/${prevMatch.id}`);
    }
  };

  // Navigate to next match
  const goToNextMatch = () => {
    const currentIndex = matches.findIndex(m => m.id === match.id);
    if (currentIndex < matches.length - 1) {
      const nextMatch = matches[currentIndex + 1];
      setCurrentMatchId(nextMatch.id);
      navigate(`/match/${nextMatch.id}`);
    }
  };

  // Toggle fullscreen function
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error(`Fullscreen request failed: ${err.message} (${err.name})`);
      // Optionally show an error message to the user
      alert("Fullscreen mode is not supported or was denied.");
      setIsFullscreen(false); // Ensure state reflects reality
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Match header */}
      <div className="bg-gray-100 dark:bg-gray-800 p-4 border-b dark:border-gray-700">
        <div className="flex justify-between items-center max-w-3xl mx-auto">
          {/* Left: Previous Match Button or Placeholder */}
          <div className="w-16 flex justify-start">
            <button
              onClick={goToPreviousMatch}
              disabled={matches.findIndex(m => m.id === match.id) === 0}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
              title="Previous Match"
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          {/* Center: Title and Info */}
          <div className="text-center flex-grow">
            <h1 className="text-lg font-bold">{match.homeTeam?.name || 'TBD'} vs {match.awayTeam?.name || 'TBD'}</h1>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {match.round.startsWith('group-')
                ? `Group ${match.round.replace('group-', '')}`
                : match.round.startsWith('knockout-')
                  ? `Knockout Stage`
                  : match.round}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Game {match.gameNumber} • {match.fieldName}
            </div>
          </div>

          {/* Right: Next Match Button (Done button removed) */}
          <div className="w-16 flex justify-end">
            <button
              onClick={goToNextMatch}
              disabled={matches.findIndex(m => m.id === match.id) === matches.length - 1}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
              title="Next Match"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-3xl mx-auto">
          {/* Timer - Enlarged for iPad optimization */}
          <Card className="mb-4">
            <div className="flex flex-col items-center p-4">
              <div className="text-md font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center">
                <Timer size={20} className="mr-2" />
                Match Timer
              </div>

              <div
                className="text-[16rem] leading-none font-bold mb-6 cursor-pointer hover:text-blue-500 transition-colors"
                onClick={handleTimerClick}
                title="Click to adjust time manually"
              >
                {formatTime(remainingTime)}
              </div>

              <div className="flex space-x-3">
                {/* Show Done button if completed, otherwise Start/Resume */}
                {match.status === 'completed' ? (
                  <Button
                    primary
                    icon={<CheckSquare size={24} />}
                    onClick={() => navigate('/schedule')}
                    className="text-lg py-3 px-6"
                    title="Done Editing"
                  >
                    Done
                  </Button>
                ) : !isRunning ? (
                  <Button
                    primary
                    icon={<PlayCircle size={24} />}
                    onClick={startMatch}
                    disabled={remainingTime === 0} // Disable if time is up
                    className="text-lg py-3 px-6"
                  >
                    {match.status === 'scheduled' ? 'Start Match' : 'Resume'}
                  </Button>
                ) : (
                  <Button
                    secondary
                    icon={<PauseCircle size={24} />}
                    onClick={pauseMatch}
                    className="text-lg py-3 px-6"
                  >
                    Pause
                  </Button>
                )}

                {/* Reset Match Button (if completed) or End Match Button */}
                <Button
                  tertiary
                  icon={<StopCircle size={24} />}
                  onClick={() => {
                    if (match.status === 'completed') {
                      // Reset the entire match state
                      if (window.confirm('Are you sure you want to reset this match? Scores and events will be cleared.')) {
                        updateMatch(match.id, {
                          homeScore: 0,
                          awayScore: 0,
                          events: [],
                          status: 'scheduled'
                        });
                        // Also reset local timer state
                        resetTimer(); 
                      }
                    } else {
                      // Show confirmation for ending the match
                      setShowEndConfirm(true);
                    }
                  }}
                  disabled={match.status === 'scheduled'}
                  className="text-lg py-3 px-6"
                >
                  {match.status === 'completed' ? 'Reset Match' : 'End Match'}
                </Button>
              </div>

              {/* Add/Remove Minute Buttons */}
              <div className="flex space-x-2 mt-4">
                <Button
                  small
                  secondary
                  icon={<PlusCircle size={16} />}
                  onClick={addOneMinute}
                  disabled={match.status === 'completed'}
                  className="py-2 px-4"
                >
                  +1 min
                </Button>
                <Button
                  small
                  secondary
                  icon={<MinusCircle size={16} />}
                  onClick={removeOneMinute}
                  disabled={match.status === 'completed' || remainingTime < 60}
                  className="py-2 px-4"
                >
                  -1 min
                </Button>
              </div>

              {/* Confirmation dialog */}
              {showEndConfirm && (
                <div className="mt-4 p-3 border border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-900/20 rounded-lg">
                  <p className="text-sm mb-3">Are you sure you want to end this match?</p>
                  <div className="flex justify-end space-x-2">
                    <Button small tertiary onClick={() => setShowEndConfirm(false)}>Cancel</Button>
                    <Button small danger icon={<Flag size={14} />} onClick={endMatch}>End Match</Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Score */}
          <Card className="mb-4">
            <div className="flex justify-between items-center p-4">
              {/* Home team */}
              <div className="flex-1 flex flex-col items-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: match.homeTeam.color }}>
                  <span className="text-white font-bold text-lg">
                    {match.homeTeam.name.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="mt-2 font-medium text-center">{match.homeTeam.name}</div>
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={handleHomeGoalClick}
                    className="px-4 py-2 text-md rounded-full border border-gray-300 dark:border-gray-700
                      hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    + Goal
                  </button>
                  <button
                    onClick={removeHomeGoal}
                    disabled={match.homeScore <= 0}
                    className="px-4 py-2 text-md rounded-full border border-gray-300 dark:border-gray-700
                      hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    - Goal
                  </button>
                </div>
              </div>

              {/* Score */}
              <div className="flex-1 flex flex-col items-center">
                <div className="text-6xl font-bold">
                  {match.homeScore} - {match.awayScore}
                </div>
                <div className="text-md text-gray-500 dark:text-gray-400 mt-2">
                  {match.status === 'completed'
                    ? 'Final Score'
                    : match.status === 'in-progress'
                      ? 'Current Score'
                      : 'Match Scheduled'}
                </div>
              </div>

              {/* Away team */}
              <div className="flex-1 flex flex-col items-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: match.awayTeam.color }}>
                  <span className="text-white font-bold text-lg">
                    {match.awayTeam.name.slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="mt-2 font-medium text-center">{match.awayTeam.name}</div>
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={handleAwayGoalClick}
                    className="px-4 py-2 text-md rounded-full border border-gray-300 dark:border-gray-700
                      hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    + Goal
                  </button>
                  <button
                    onClick={removeAwayGoal}
                    disabled={match.awayScore <= 0}
                    className="px-4 py-2 text-md rounded-full border border-gray-300 dark:border-gray-700
                      hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    - Goal
                  </button>
                </div>
              </div>
            </div>
            
            {/* Home team scorer selection popup - Use up-to-date team data */}
            {showHomeScorers && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" 
                   style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md m-4" 
                     style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                  <h3 className="text-xl font-semibold mb-4 text-center">Select Goal Scorer</h3>
                  {(() => {
                    // Fetch current team data again for rendering
                    const currentHomeTeam = teams.find(t => t.id === match.homeTeam?.id);
                    const homePlayers = currentHomeTeam?.players || [];
                    
                    return homePlayers.length > 0 ? (
                      <div className="max-h-96 overflow-y-auto">
                        {homePlayers.map(player => (
                          <button
                            key={player.id}
                            onClick={() => addHomeGoal(player.id)}
                            className="w-full text-left p-4 my-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center border border-gray-200 dark:border-gray-700"
                          >
                            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-200 dark:bg-gray-700 mr-4 flex-shrink-0" 
                                 style={{ backgroundColor: currentHomeTeam?.color || '#ccc' }}>
                              <span className="font-semibold text-white">{player.number || '#'}</span>
                            </div>
                            <div>
                              <div className="font-semibold text-lg">{player.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{player.position}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">No players available</div>
                    );
                  })()}
                  <div className="mt-6 flex justify-end space-x-2">
                    <Button 
                      secondary
                      onClick={() => addHomeGoal(null)}
                      className="mr-2"
                    >
                      Unknown Player
                    </Button>
                    <Button
                      tertiary
                      onClick={() => setShowHomeScorers(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Away team scorer selection popup - Use up-to-date team data */}
            {showAwayScorers && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" 
                   style={{ zIndex: 9999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md m-4" 
                     style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                  <h3 className="text-xl font-semibold mb-4 text-center">Select Goal Scorer</h3>
                  {(() => {
                    // Fetch current team data again for rendering
                    const currentAwayTeam = teams.find(t => t.id === match.awayTeam?.id);
                    const awayPlayers = currentAwayTeam?.players || [];

                    return awayPlayers.length > 0 ? (
                      <div className="max-h-96 overflow-y-auto">
                        {awayPlayers.map(player => (
                          <button
                            key={player.id}
                            onClick={() => addAwayGoal(player.id)}
                            className="w-full text-left p-4 my-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center border border-gray-200 dark:border-gray-700"
                          >
                            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-200 dark:bg-gray-700 mr-4 flex-shrink-0" 
                                 style={{ backgroundColor: currentAwayTeam?.color || '#ccc' }}>
                              <span className="font-semibold text-white">{player.number || '#'}</span>
                            </div>
                            <div>
                              <div className="font-semibold text-lg">{player.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{player.position}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">No players available</div>
                    );
                  })()}
                  <div className="mt-6 flex justify-center space-x-4">
                    <Button 
                      secondary
                      onClick={() => addAwayGoal(null)}
                      className="px-6"
                    >
                      Unknown Player
                    </Button>
                    <Button
                      tertiary
                      onClick={() => setShowAwayScorers(false)}
                      className="px-6"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Match Events */}
          <Card>
            <div className="p-4">
              <h2 className="text-lg font-semibold mb-3">Match Events</h2>

              {match.events.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                  No events yet
                </div>
              ) : (
                <div className="space-y-2">
                  {match.events.map((event, index) => {
                    // Fetch current team data for accurate player name lookup
                    const currentHomeTeam = teams.find(t => t.id === match.homeTeam?.id);
                    const currentAwayTeam = teams.find(t => t.id === match.awayTeam?.id);
                    const team = event.team === 'home' ? currentHomeTeam : currentAwayTeam;
                    
                    // Find the player who scored the goal (if available)
                    let playerName = "Unknown Player";
                    let playerNumber = "";
                    
                    if (event.type === 'goal' && event.playerId) {
                      const player = team?.players?.find(p => p.id === event.playerId);
                      if (player) {
                        playerName = player.name;
                        playerNumber = player.number ? `#${player.number}` : '';
                      }
                    }
                    
                    return (
                      <div key={index} className="flex items-center py-3 border-b dark:border-gray-700 last:border-0">
                        <div className="w-8">
                          {event.type === 'goal' && <span className="text-xl">⚽</span>}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            {event.type === 'goal' ? 'Goal' : event.type}
                          </div>
                          <div className="text-sm flex items-center">
                            {/* Color indicator */}
                            <div 
                              className="w-2 h-2 rounded-full inline-block mr-1.5 flex-shrink-0"
                              style={{ backgroundColor: team?.color || '#ccc' }} 
                            />
                            <span className="font-semibold">{team?.name || 'Team'}</span>
                            {event.type === 'goal' && (
                              <span className="ml-1">
                                - {playerName} {playerNumber && <span className="text-gray-500">({playerNumber})</span>}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(event.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Right side buttons: Fullscreen (mobile/tablet) + Next Match */}
      <div className="flex items-center space-x-1 w-10 justify-end">
        {/* Fullscreen button - visible on small/medium screens only */}
        <button
          onClick={toggleFullscreen}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 inline-flex lg:hidden" 
          title="Toggle Fullscreen"
        >
          {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
        </button>
        
        {/* Next match button */}
        <button
          onClick={goToNextMatch}
          disabled={matches.findIndex(m => m.id === match.id) === matches.length - 1}
          className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default MatchPlayPage;
